import { Link } from "react-router-dom";
import { Stack, Group, Title, Text, Badge, Divider, Paper, ThemeIcon, SimpleGrid, Container, ActionIcon, Button, ScrollArea, Center, Loader, Space, useMantineTheme, Menu } from "@mantine/core";
import { IconClock, IconCalendar, IconRepeat, IconUser, IconPencil, IconTrash, IconDots, IconChevronLeft, IconClipboardCopy, IconCalendarMinus, IconCheck, IconClockPause, IconClockPlay, IconCalendarTime, IconCircleCheck, IconFileDescription } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment-timezone";

import { WorkOrderScheduleDto, RecurrenceType, DaysOfWeek } from "../../models/workorderschedule.models";
import { UpcomingExecutions } from "./UpcomingExecutions";
import { PastExecutions } from "./PastExecutions";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './ScheduleDetailView.module.css';
import { getScheduleStatusColor, getScheduleStatusDescription } from "../../functions/schedule.functions";

interface ScheduleDetailViewProps {
	schedule: WorkOrderScheduleDto | null | undefined;
	isLoading: boolean;
	onEdit?: () => void;
	onDelete?: () => void;
	onActivate?: () => void;
	onDeactivate?: () => void;
	onClose?: () => void;
}

export const ScheduleDetailView = ({ schedule, isLoading, onEdit, onDelete, onActivate, onDeactivate, onClose }: ScheduleDetailViewProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
		<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
			<Center>
				<Loader size="sm" mt="lg" />
			</Center>
		</ScrollArea>
	</Paper>
	
	if (!schedule) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
		<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
			<Stack justify="center" align="center" gap="md" mt="xl">
				<ThemeIcon size={100} radius={100} variant="light" color="gray">
					<IconClock size={60} />
				</ThemeIcon>
				<Text c="dimmed">Select a schedule to view details</Text>
			</Stack>
			<Space h="md" />
		</ScrollArea>
	</Paper>

	const getRecurrenceTypeLabel = (type: RecurrenceType) => {
		switch (type) {
			case RecurrenceType.OneTime:
				return "One Time";
			case RecurrenceType.Daily:
				return "Daily";
			case RecurrenceType.Weekly:
				return "Weekly";
			case RecurrenceType.Monthly:
				return "Monthly";
			default:
				return "Unknown";
		}
	};

	const getRecurrenceDescription = () => {
		const pattern = schedule.RecurrencePattern;

		if (schedule.RecurrenceType === RecurrenceType.OneTime) {
			return `On ${moment.utc(schedule.StartDate).local().format("LL")}`;
		}

		if (schedule.RecurrenceType === RecurrenceType.Daily) {
			return "Every day";
		}

		if (schedule.RecurrenceType === RecurrenceType.Weekly) {
			const days = pattern.DaysOfWeek?.map((d) => {
				const dayObj = DaysOfWeek.find((dw) => dw.value === d);
				return dayObj?.label;
			}).join(", ");
			const interval = pattern.WeekInterval && pattern.WeekInterval > 1 ? ` every ${pattern.WeekInterval} weeks` : "";
			return `${days}${interval}`;
		}

		if (schedule.RecurrenceType === RecurrenceType.Monthly) {
			if (pattern.DayOfMonth) {
				return `The ${pattern.DayOfMonth}${getSuffix(pattern.DayOfMonth)} of every month`;
			}

			if (pattern.WeekOfMonth && pattern.DayOfWeekForMonthly) {
				const weekLabel = ["1st", "2nd", "3rd", "4th", "Last"][pattern.WeekOfMonth - 1];
				const dayObj = DaysOfWeek.find((d) => d.value === pattern.DayOfWeekForMonthly);
				return `The ${weekLabel} ${dayObj?.label} of every month`;
			}
		}

		return "Unknown";
	};

	const getSuffix = (day: number) => {
		if (day > 3 && day < 21) return "th";
		switch (day % 10) {
			case 1:
				return "st";
			case 2:
				return "nd";
			case 3:
				return "rd";
			default:
				return "th";
		}
	};

	return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
		<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
			<Stack gap={0} mb="md" justify="center" className={classes['header']}>
				<Group justify="space-between" px="20" wrap="nowrap">
					<Group gap="xs" wrap="nowrap">
						<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
							<IconChevronLeft />
						</ActionIcon>
						<Stack gap={0}>
							<Title order={isMobile ? 6 : 3}>{schedule.ScheduleName}</Title>
							{!schedule.Deleted &&
							<Text fz="xs" c="dimmed">Created on {moment.utc(schedule.Created).local().format("LL")}</Text>
							}
							{schedule.Deleted &&
							<Text fz="xs" c="dimmed">Deleted on {moment.utc(schedule.Deleted).local().format("LL")}</Text>
							}
						</Stack>
					</Group>
					<Group gap="xs">
						<Button.Group mr={-10}>
							{schedule.Deleted == null && <>
								{!isMobile &&
									<Button onClick={onEdit} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
								}
								{isMobile &&
									<Button onClick={onEdit} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
								}
								<Divider orientation="vertical" />
								<Menu position="bottom-end" shadow="md" radius="lg">
									<Menu.Target>
										<Button variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
									</Menu.Target>
									<Menu.Dropdown>
										{schedule.IsActive ? (
											<Menu.Item onClick={onDeactivate} leftSection={<IconClockPause stroke={1.5} />}>Deactivate</Menu.Item>
										) : (
											<Menu.Item onClick={onActivate} leftSection={<IconClockPlay stroke={1.5} />}>Activate</Menu.Item>
										)}
										<Menu.Divider />
										<Menu.Item onClick={onDelete} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
									</Menu.Dropdown>
								</Menu>
							</>}
						</Button.Group>
					</Group>
				</Group>
			</Stack>

			<Container px="20" pb="xl">
				<SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
					{schedule.Description && <Stack gap={0}>
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
						<Text fz="sm">{schedule.Description}</Text>
					</Stack>}

					<Stack gap={4}>
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Status</Text>
						<Badge size="lg" variant="light" radius="sm" color={getScheduleStatusColor(schedule)}>{getScheduleStatusDescription(schedule)}</Badge>
					</Stack>
				</SimpleGrid>

				<Stack gap={4}>
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Work Order Template</Text>
					<Link to={{ pathname: `/workordertemplates/${schedule.WorkOrderTemplateId}` }} className={classes['link']}>
						<Badge size="lg" fz="sm" radius="sm" leftSection={<IconClipboardCopy size={14} />}>{schedule.TemplateName}</Badge>
					</Link>
				</Stack>

				{/* <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md" mt="lg">
					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="" size="lg" radius="md">
								<IconFileDescription size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Description
								</Text>
								<Text fw={500}>{schedule.Description}</Text>
							</div>
						</Group>
					</Paper>

					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color={getScheduleStatusColor(schedule)} size="lg" radius="md">
								<IconCircleCheck size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Status
								</Text>
								<Text fw={500}>{getScheduleStatusDescription(schedule)}</Text>
							</div>
						</Group>
					</Paper>

					<Paper component={Link} to={{ pathname: `/workordertemplates/${schedule.WorkOrderTemplateId}` }} p="md" radius="md" className={classes['infoPaperLink']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="" size="lg" radius="md">
								<IconClipboardCopy size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Work Order Template
								</Text>
								<Text size="sm">
									{schedule.TemplateName}
								</Text>
							</div>
						</Group>
					</Paper>
				</SimpleGrid> */}
				
				
				<Divider my="md" />

				{/* <Text fz="xs" fw="700" tt="uppercase" c="dimmed">Schedule Details</Text> */}

				<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mt="md">
					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="blue" size="lg" radius="md">
								<IconRepeat size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Recurrence
								</Text>
								<Text fw={500}>{getRecurrenceTypeLabel(schedule.RecurrenceType)}</Text>
								<Text size="sm" c="dimmed">{getRecurrenceDescription()}</Text>
							</div>
						</Group>
					</Paper>

					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="blue" size="lg" radius="md">
								<IconClock size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Execution Time
								</Text>
								<Text fw={500}>{moment.utc(schedule.TimeOfExecution, "HH:mm:ss").local().format("h:mm A")}</Text>
							</div>
						</Group>
					</Paper>

					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="teal" size="lg" radius="md">
								<IconCalendar size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Schedule Period
								</Text>
								<Text fw={500}>{moment.utc(schedule.StartDate).local().format("LL")}</Text>
								<Text size="sm" c="dimmed">
									to {schedule.EndDate ? moment.utc(schedule.EndDate).local().format("LL") : "Forever"}
								</Text>
							</div>
						</Group>
					</Paper>

					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="cyan" size="lg" radius="md">
								<IconCalendarTime size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Last Executed
								</Text>
								{schedule.LastExecuted ? (
									<>
										<Text fw={500}>{moment.utc(schedule.LastExecuted).local().format("LL")}</Text>
										<Text size="sm" c="dimmed">{moment.utc(schedule.LastExecuted).local().fromNow()}</Text>
									</>
								) : (
									<Text fw={500}>Never</Text>
								)}
							</div>
						</Group>
					</Paper>

					{/* <Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="cyan" size="lg" radius="md">
								<IconUser size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Created By
								</Text>
								<Text fw={500}>{schedule.CreatedBy?.Name || "Unknown"}</Text>
								<Text size="sm" c="dimmed">{moment.utc(schedule.Created).local().format("LL")}</Text>
							</div>
						</Group>
					</Paper> */}
					
					{schedule.ExcludedDaysOfWeek && schedule.ExcludedDaysOfWeek.length > 0 && (
					<Paper p="md" radius="md" className={classes['infoPaper']}>
						<Group gap="xs" wrap="nowrap" align="start">
							<ThemeIcon variant="light" color="orange" size="lg" radius="md">
								<IconCalendarMinus size={18} />
							</ThemeIcon>
							<div>
								<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
									Excluded Days
								</Text>
								{schedule.ExcludedDaysOfWeek.map((day) => {
									const dayObj = DaysOfWeek.find((d) => d.value === day);
									return (
										<Text fw={500}>{dayObj?.label}</Text>
									);
								})}
							</div>
						</Group>
					</Paper>
					)}
				</SimpleGrid>

				{/* {schedule.ExcludedDaysOfWeek && schedule.ExcludedDaysOfWeek.length > 0 && (
					<>
						<Divider my="md" />
						<div>
							<Text fw={500} mb="xs">
								Excluded Days of Week:
							</Text>
							<Group gap="xs">
								{schedule.ExcludedDaysOfWeek.map((day) => {
									const dayObj = DaysOfWeek.find((d) => d.value === day);
									return (
										<Badge key={day} variant="outline" color="gray">
											{dayObj?.label}
										</Badge>
									);
								})}
							</Group>
						</div>
					</>
				)} */}

				{!schedule.Deleted && <>
					<Divider my="md" />
					<UpcomingExecutions	
						upcomingExecutions={schedule.UpcomingExecutions} 
						isLoading={false} 
						lastExecuted={schedule.LastExecuted} 
					/>
				</>}

				<Divider my="md" />

				<PastExecutions 
					pastExecutions={schedule.PastExecutions} 
					isLoading={false} 
				/>

				<Divider my="md" />

				<SimpleGrid cols={isMobile ? 1 : 2}>
					<CrudUserDate label="Created" userId={schedule.CreatedBy.UserId} userName={schedule.CreatedBy.Name} date={schedule.Created} useNewLayout />
					{schedule.UpdatedBy && !schedule.Deleted && <>
						<CrudUserDate label="Updated" userId={schedule.UpdatedBy.UserId} userName={schedule.UpdatedBy.Name} date={schedule.Updated ?? null} useNewLayout />
					</>
					}
					{schedule.DeletedBy && <>
						<CrudUserDate label="Deleted" userId={schedule.DeletedBy.UserId} userName={schedule.DeletedBy.Name} date={schedule.Deleted ?? null} useNewLayout />
					</>
					}
				</SimpleGrid>
			</Container>
		</ScrollArea>
	</Paper>
};
